import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Plus, AlertTriangle, Pill, Heart, Shield, FileText } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Cross-Module Intervention Button
 * 
 * Allows adding interventions to a patient's medical record from any module.
 * This enables seamless system connectivity where family concerns, medication issues,
 * or compliance flags can instantly create actionable interventions in the EHR.
 */

const INTERVENTION_TYPES = [
  { value: "medication_issue", label: "Medication Issue", icon: Pill, color: "text-blue-400" },
  { value: "symptom_report", label: "Symptom Report", icon: Heart, color: "text-red-400" },
  { value: "care_concern", label: "Care Concern", icon: Shield, color: "text-amber-400" },
  { value: "safety_issue", label: "Safety Issue", icon: AlertTriangle, color: "text-red-500" },
  { value: "family_communication", label: "Family Communication", icon: FileText, color: "text-purple-400" }
];

const SEVERITY_OPTIONS = [
  { value: "critical", label: "Critical", color: "bg-red-600 text-white" },
  { value: "high", label: "High", color: "bg-orange-500 text-white" },
  { value: "medium", label: "Medium", color: "bg-yellow-500 text-black" },
  { value: "low", label: "Low", color: "bg-gray-400 text-white" }
];

export function AddToRecordButton({ 
  chartId, 
  patientName, 
  sourceModule,
  sourceEventId = "",
  prefillType = "",
  prefillDescription = "",
  className = "",
  variant = "outline",
  size = "sm"
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    intervention_type: prefillType || "care_concern",
    description: prefillDescription || "",
    severity: "medium",
    reported_by: ""
  });

  const handleSubmit = async () => {
    if (!form.description.trim()) {
      toast.error("Please provide a description");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        chart_id: chartId,
        source_module: sourceModule,
        source_event_id: sourceEventId,
        intervention_type: form.intervention_type,
        description: form.description,
        severity: form.severity,
        reported_by: form.reported_by || "Clinical Staff"
      };

      const res = await fetch(`${API_URL}/api/ehr/scheduling/interventions/quick-add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success("Intervention added to medical record", {
          description: `${data.linked_to_medication ? "Linked to medication module. " : ""}${data.linked_to_care_plan ? "Flagged for care plan review." : ""}`
        });
        setOpen(false);
        setForm({
          intervention_type: "care_concern",
          description: "",
          severity: "medium",
          reported_by: ""
        });
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add intervention");
      }
    } catch (error) {
      console.error("Error adding intervention:", error);
      toast.error("Error connecting to server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={`border-cyan-600 text-cyan-400 hover:bg-cyan-600/20 ${className}`}
        data-testid="add-to-record-btn"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add to Record
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-cyan-400" />
              Add Intervention to Medical Record
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {patientName && (
                <span className="text-cyan-300 font-medium">Patient: {patientName}</span>
              )}
              {" • "}Source: {sourceModule.replace("_", " ")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Intervention Type */}
            <div>
              <Label>Intervention Type *</Label>
              <Select 
                value={form.intervention_type} 
                onValueChange={(v) => setForm({...form, intervention_type: v})}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {INTERVENTION_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-slate-600">
                      <div className="flex items-center gap-2">
                        <type.icon className={`w-4 h-4 ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Severity */}
            <div>
              <Label>Severity *</Label>
              <div className="flex gap-2 mt-2">
                {SEVERITY_OPTIONS.map((opt) => (
                  <Badge
                    key={opt.value}
                    className={`cursor-pointer px-3 py-1 ${
                      form.severity === opt.value 
                        ? opt.color 
                        : "bg-slate-600 text-slate-300 hover:bg-slate-500"
                    }`}
                    onClick={() => setForm({...form, severity: opt.value})}
                  >
                    {opt.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Description *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="Describe the concern, issue, or observation..."
                className="bg-slate-700 border-slate-600 min-h-[100px]"
              />
            </div>

            {/* Reported By */}
            <div>
              <Label>Reported By</Label>
              <Select 
                value={form.reported_by} 
                onValueChange={(v) => setForm({...form, reported_by: v})}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Select reporter" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="Clinical Staff" className="text-white hover:bg-slate-600">Clinical Staff</SelectItem>
                  <SelectItem value="Family Member" className="text-white hover:bg-slate-600">Family Member</SelectItem>
                  <SelectItem value="Patient" className="text-white hover:bg-slate-600">Patient</SelectItem>
                  <SelectItem value="Caregiver" className="text-white hover:bg-slate-600">Caregiver</SelectItem>
                  <SelectItem value="System Alert" className="text-white hover:bg-slate-600">System Alert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Info Note */}
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-sm">
              <p className="text-cyan-300">
                <strong>Cross-Module Integration:</strong>
              </p>
              <ul className="text-slate-400 mt-1 space-y-1">
                <li>• Medication issues auto-link to medication alerts</li>
                <li>• Symptom reports flag care plan for review</li>
                <li>• All interventions logged to patient activity</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setOpen(false)} 
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={loading || !form.description.trim()}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {loading ? "Adding..." : "Add to Medical Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Quick Add Intervention Panel
 * 
 * A compact inline version for embedding in cards/lists
 */
export function QuickInterventionPanel({
  chartId,
  patientName,
  sourceModule,
  onSuccess
}) {
  const [type, setType] = useState("care_concern");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const handleQuickAdd = async () => {
    if (!description.trim()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/ehr/scheduling/interventions/quick-add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          chart_id: chartId,
          source_module: sourceModule,
          intervention_type: type,
          description: description,
          severity: "medium",
          reported_by: "Clinical Staff"
        })
      });

      if (res.ok) {
        toast.success("Added to patient record");
        setDescription("");
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      toast.error("Failed to add intervention");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-700/50 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="bg-slate-600 border-slate-500 w-40 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-700 border-slate-600">
            {INTERVENTION_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} className="text-white text-xs">
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Quick note..."
          className="flex-1 bg-slate-600 border-slate-500 rounded px-2 py-1 text-sm text-white placeholder-slate-400"
        />
        <Button
          size="sm"
          onClick={handleQuickAdd}
          disabled={loading || !description.trim()}
          className="bg-cyan-600 hover:bg-cyan-700 h-8 px-3"
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        Quick add to {patientName || "patient"}&apos;s record
      </p>
    </div>
  );
}

export default AddToRecordButton;
